import { useEffect } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { useRouter } from 'next/router';
import pushRoute from '@utils/pushRoute';
import Paper from 'aquilla/core/atoms/Paper';
import Button from 'aquilla/core/molecules/Button';
import { useIsMobile } from '@stores/AppCommonData';
import { Spacings, mediaQuery } from '@styles/index';
import Typography from 'aquilla/core/atoms/Typography';
import useOneLinkSmartScriptV2 from '@hooks/useOneLinkSmartScriptV2';
import LottieAnimation from '@base/LottieAnimation/LottieAnimation';
import { emptyObject } from '@constants/empty';
import { getIsLoggedIn } from '@api/selectors/auth';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getAnimationPath } from '@utils/animationHelpers';
import { segment } from '@actions/analytics';
import segmentStorage from '@events/segmentStorage';
import Image from '@base/FutureImage';
import { getCardInfo, MAX_GAMES_ALLOWED_FOR_SCHOLARSHIP } from '../utils';
import { BANNER_BANNER_CLICKED, BANNER_BANNER_VIEWED } from '../constants';

const Wrapper = styled.div`
  width: 100%;
  height: 312px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  ${mediaQuery.uptoMobile} {
    height: 220px;
    overflow: hidden;
    padding: ${Spacings.SPACING_8B} ${Spacings.SPACING_4B};
  }
`;

const BGShadeWrapper = styled.div`
  width: 670px;
  height: 312px;
  position: absolute;
  ${({ $bottomRight }) =>
    $bottomRight
      ? css`
          right: 0;
          bottom: 0;
          transform: rotate(180deg);
        `
      : css`
          top: 0;
          left: 0;
        `}
`;

const ContentWrapper = styled(Paper)`
  position: relative;
  z-index: 1;
  width: 1136px;
  height: 216px;
  border-radius: ${Spacings.SPACING_2B};
  overflow: hidden;
  padding: ${Spacings.SPACING_10B} ${Spacings.SPACING_12B};
  border: 1px solid var(--color-divider);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  ${mediaQuery.uptoMobile} {
    width: 100%;
    height: 156px;
    display: flex;
    flex-direction: column;
    padding: ${Spacings.SPACING_4B};
  }
`;

const TitleWrapper = styled.div`
  ${({ $maxWidth }) =>
    $maxWidth
      ? css`
          max-width: ${$maxWidth};
          ${mediaQuery.uptoMobile} {
            max-width: ${$maxWidth};
          }
        `
      : ''}
`;

const SubTextWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-top: ${Spacings.SPACING_2B};
  p {
    color: var(--color-text-secondary);
  }
  ${mediaQuery.uptoMobile} {
    margin-top: ${Spacings.SPACING_1B};
  }
`;

const CTAWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-top: ${Spacings.SPACING_6B};
  ${mediaQuery.uptoMobile} {
    flex-grow: 1;
    align-items: flex-end;
    margin-top: ${Spacings.SPACING_4B};
  }
`;

const PrimaryCTA = styled(Button)`
  margin-right: ${Spacings.SPACING_6B};
  background-color: var(--color-i-blue-44);
  width: 157px;
  ${mediaQuery.uptoMobile} {
    margin-right: ${Spacings.SPACING_3B};
    background-color: var(--color-base-fill);
  }
`;

const IllustrationWrapper = styled.div`
  position: absolute;
  right: 0;
  bottom: 0;
  width: 400px;
  height: 215px;
  ${mediaQuery.uptoMobile} {
    top: 0;
    width: ${({ $isCompeteHour }) => ($isCompeteHour ? '108px' : '130px')};
    height: ${Spacings.SPACING_21B};
  }
`;

const AnimationContainer = styled(LottieAnimation)`
  height: 16px;
  width: 40px;
  margin-right: 8px;
`;

const SecondaryButton = styled(Button)`
  width: 157px;
`;

const AwarenessBanner = ({ block }) => {
  const {
    title,
    endTime,
    subTitle,
    startTime,
    scholarship,
    isEligibleForScholarship,
    firstCompeteTaken,
    count,
    liveTitle
  } = block?.data || emptyObject;
  const isMobile = useIsMobile();
  const {
    query: { goalSlug, goalUID }
  } = useRouter();
  const isLoggedIn = useMyInfo(getIsLoggedIn);

  const isAttemptAgain =
    isEligibleForScholarship &&
    scholarship?.gamesPlayed >= MAX_GAMES_ALLOWED_FOR_SCHOLARSHIP;

  useEffect(() => {
    if (title) {
      segment.track(BANNER_BANNER_VIEWED, {
        banner_title: title,
        banner_type: 'Compete Awareness',
        goal_uid: goalUID,
        use_segment_api: true
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { desktopImg, mwebImg, isCompeteHour } = getCardInfo({
    endTime,
    startTime,
    scholarship,
    isLoggedIn,
    isFirstGamePlayed: firstCompeteTaken,
    isEligibleForScholarship,
    isAttemptAgain
  });

  const appDownloadLink = useOneLinkSmartScriptV2();

  const handleCompeteNowClick = () => {
    segment.track(BANNER_BANNER_CLICKED, {
      cta_button: isMobile ? 'Download app' : 'Compete now',
      banner_title: title,
      goal_uid: goalUID,
      banner_type: 'Compete Awareness'
    });
    if (isMobile) {
      window.open(appDownloadLink, '_blank');
      return;
    }
    segmentStorage.setItem('lps', 'Home');
    return pushRoute({
      pathname: '/goal/[goalSlug]/[goalUID]/compete/battlefield',
      query: {
        goalSlug,
        goalUID
      }
    });
  };

  const handleLearnMoreClick = () => {
    segment.track(BANNER_BANNER_CLICKED, {
      cta_button: 'Learn more',
      banner_title: title,
      goal_uid: goalUID,
      banner_type: 'Compete Awareness'
    });
    segmentStorage.setItem('lps', 'Home');
    pushRoute({
      pathname: '/goal/[goalSlug]/[goalUID]/compete',
      query: {
        goalSlug,
        goalUID
      }
    });
  };

  const getTitleMaxWidth = () => {
    if (isCompeteHour) {
      return null;
    }
    if (!subTitle) {
      if (isMobile) {
        return '208px';
      }
      return '70%';
    }
    if (isAttemptAgain && isMobile) {
      return '163px';
    }
    if (isMobile && !isEligibleForScholarship) {
      return '152px';
    }
  };

  const getImageDimensions = () => {
    if (isMobile) {
      if (isCompeteHour) {
        return { width: 108, height: 60 };
      }
      return { width: 130, height: 84 };
    }
    return { width: 400, height: 215 };
  };

  const { width, height } = getImageDimensions();

  const renderSubTitle = () => {
    if (subTitle) {
      return subTitle;
    }
    if (count) {
      return `${count} learners competing`;
    }
  };

  return (
    <Wrapper>
      <BGShadeWrapper>
        <Image
          src="compete/compete-banner-bg-shade.svg"
          height={isMobile ? 220 : 312}
          width={isMobile ? 360 : 670}
          alt=""
        />
      </BGShadeWrapper>
      <ContentWrapper>
        <TitleWrapper $maxWidth={getTitleMaxWidth()}>
          <Typography variant={isMobile ? 'h5' : 'h3'}>
            {isCompeteHour ? liveTitle : title}
          </Typography>
        </TitleWrapper>
        <SubTextWrapper>
          {!isEligibleForScholarship && (subTitle || count) && (
            <AnimationContainer
              src={getAnimationPath('competeJoiningMembers.json')}
              loop
            />
          )}
          <Typography variant={isMobile ? 'p3' : 'p1'}>
            {renderSubTitle()}
          </Typography>
        </SubTextWrapper>
        <CTAWrapper>
          <PrimaryCTA
            variant="primary"
            label={isMobile ? 'Download app' : 'Compete now'}
            size={isMobile ? 'medium' : 'large'}
            onClick={handleCompeteNowClick}
          />
          <SecondaryButton
            variant="secondary"
            label="Learn more"
            size={isMobile ? 'medium' : 'large'}
            onClick={handleLearnMoreClick}
          />
        </CTAWrapper>
        <IllustrationWrapper $isCompeteHour={isCompeteHour}>
          <Image
            src={isMobile ? mwebImg : desktopImg}
            height={height}
            width={width}
            alt=""
          />
        </IllustrationWrapper>
      </ContentWrapper>
      {!isMobile && (
        <BGShadeWrapper $bottomRight>
          <Image
            src="compete/compete-banner-bg-shade.svg"
            height={312}
            width={670}
            alt=""
          />
        </BGShadeWrapper>
      )}
    </Wrapper>
  );
};

export default AwarenessBanner;
